import { Operations } from "../../../services/ItemServices";

export const DESTINATION_SCREEN_KEY = "destination";
export const DESTINATION_DEPLOYMENTS_SCREEN_KEY = "deployments";
export const DESTINATION_DEPLOYMENT_SCREEN_KEY = "deployment";
export const DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY = "warehouse-views";
export const DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY = "entity-deployments";
export const DESTINATION_SCHEDULES_SCREEN_KEY = "schedule";
export const ENDPOINT_AUTHENTICATION_SCREEN_KEY = "authentication";

export const DESTINATION_NAVIGATIONS = [
  { key: DESTINATION_SCREEN_KEY, displayName: "Destination", icon: "target", sequence: 10 },
  { key: DESTINATION_DEPLOYMENTS_SCREEN_KEY, displayName: "Deployments", icon: "target", sequence: 20 }
];

export const DESTINATION_SCHEDULE_NAVIGATION = {
  key: DESTINATION_SCHEDULES_SCREEN_KEY,
  displayName: "Schedule",
  icon: "calendar",
  sequence: 40
};

export const DESTINATION_DEPLOYMENT_NAVIGATION = {
  key: DESTINATION_DEPLOYMENT_SCREEN_KEY,
  displayName: "Deployment",
  icon: "target",
  sequence: 30,
  hideInNavigation: true
};

export const DESTINATION_ENTITY_DEPLOYMENTS_NAVIGATION = {
  key: DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY,
  displayName: "Entity Scope",
  icon: "target",
  sequence: 35,
  hideInNavigation: true
};

export const DESTINATION_WAREHOUSE_VIEWS_NAVIGATION = {
  key: DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY,
  displayName: "Views",
  icon: "view",
  sequence: 35
};

export const DESTINATION_RESOURCE_DEFAULTS = {
  warehouse: {
    managerKey: "warehouse",
    itemName: "Warehouse",
    label: "Warehouse Destination",
    deploymentLabel: "Deployment",
    deploymentCollection: "WarehouseDeployments",
    checkOperationName: Operations.WarehouseItem?.WarehouseCheck || "WarehouseCheck",
    productKeys: ["WarehouseDestination"],
    resourceKeys: ["AzureSQLWarehouse", "MongoDBWarehouse", "SnowflakeWarehouse", "AzureSynapseWarehouse"]
  },
  fileStorage: {
    managerKey: "fileStorage",
    itemName: "FileStorage",
    label: "File Destination",
    deploymentLabel: "Deployment",
    deploymentCollection: "StorageDeployments",
    checkOperationName: Operations.FileStorageItem?.FileStorageCheck || "FileStorageCheck",
    productKeys: ["FileStorageDestination", "FileDestination"],
    resourceKeys: ["SFTPStorage", "AzureBlobStorage", "OpenTextTradingGridStorage", "AS2LogicAppsStorage", "Microsoft365DriveStorage"]
  },
  externalSystem: {
    managerKey: "externalSystem",
    itemName: "ExternalSystem",
    label: "External System Destination",
    deploymentLabel: "Deployment",
    deploymentCollection: "ExternalSystemDeployments",
    checkOperationName: Operations.ExternalSystemItem?.ExternalSystemCheck || "ExternalSystemCheck",
    validateOperationName: Operations.ExternalSystemItem?.ExternalSystemValidate || "ExternalSystemValidate",
    previewOperationName: Operations.ExternalSystemItem?.ExternalSystemPreview || "ExternalSystemPreview",
    productKeys: ["ExternalSystemSync"],
    resourceKeys: ["OdooSync", "BusinessCentralSync", "GreatPlainsSync", "OpenTextTradingGridSync"]
  },
  schemaAPI: {
    managerKey: "schemaAPI",
    itemName: "SchemaAPI",
    label: "Endpoint",
    deploymentLabel: "Endpoint Deployment",
    deploymentCollection: "Deployments",
    deploymentItemName: "SchemaAPIDeployment",
    checkOperationName: Operations.SchemaAPIItem?.APIEndpointCheck || "APIEndpointCheck",
    deployOperationName: Operations.SchemaAPIItem?.APIEndpointDeploy || "APIEndpointDeploy",
    dropOperationName: Operations.SchemaAPIItem?.APIEndpointDrop || "APIEndpointDrop",
    loadScopeOperationName: Operations.SchemaAPIItem?.LoadAPIEndpointDeploymentEntities || "LoadAPIEndpointDeploymentEntities",
    entityObjectSelectDataOperationName: Operations.SchemaAPIItem?.SelectDataAPIEndpointDeploymentEntityObject || "SelectDataAPIEndpointDeploymentEntityObject",
    checkAuthenticationOperationName: Operations.SchemaAPIItem?.CheckAPIEndpointAuthentication || "CheckAPIEndpointAuthentication",
    createTokenOperationName: Operations.SchemaAPIItem?.CreateAPIEndpointPersonalAccessToken || "CreateAPIEndpointPersonalAccessToken",
    revokeTokenOperationName: Operations.SchemaAPIItem?.RevokeAPIEndpointPersonalAccessToken || "RevokeAPIEndpointPersonalAccessToken",
    saveBasicClientAccessOperationName: Operations.SchemaAPIItem?.SaveAPIEndpointBasicClientAccess || "SaveAPIEndpointBasicClientAccess",
    downloadBasicClientAccessScriptOperationName: Operations.SchemaAPIItem?.DownloadAPIEndpointBasicClientAccessScript || "DownloadAPIEndpointBasicClientAccessScript",
    downloadMcpOAuthSetupScriptOperationName: Operations.SchemaAPIItem?.DownloadAPIEndpointMcpOAuthSetupScript || "DownloadAPIEndpointMcpOAuthSetupScript",
    productKeys: ["Endpoint", "ApiEndpoint", "McpEndpoint"],
    resourceKeys: ["APIEndpoint", "RESTAPIEndpoint", "OpenAPIEndpoint", "MCPDataQueryEndpoint"],
    supportsSchedules: false,
    supportsManualRun: false,
    isEndpoint: true
  }
};

const DESTINATION_LINKED_ITEM_NAMES = new Set(["warehouse", "filestorage", "externalsystem", "schemaapi"]);
let destinationDeploymentClientKeySequence = 0;

export const normalizeCompare = (value) => String(value ?? "").trim().toLowerCase();

const DESTINATION_STATE_LABELS = {
  0: "Unknown",
  1: "Normal",
  2: "Online",
  3: "Offline",
  4: "Maintenance",
  90: "Disabled",
  99: "Error"
};

const DESTINATION_STATE_VALUES_BY_LABEL = Object.entries(DESTINATION_STATE_LABELS)
  .reduce((lookup, [value, label]) => ({
    ...lookup,
    [normalizeCompare(label)]: value
  }), {});

const SCHEMA_DESTINATION_STATUS_VALUES_BY_LABEL = {
  unavailable: "0",
  unknown: "0",
  online: "1",
  offline: "2"
};

const FILE_PACKAGING_MODE_LABELS = {
  1: "Batch",
  2: "Per Document"
};

const FILE_PACKAGING_MODE_VALUES_BY_LABEL = {
  batch: "1",
  document: "2",
  "per document": "2"
};

export const DESTINATION_MANUAL_RUN_PROCESSOR_TYPES = Object.freeze({
  realTime: "1",
  async: "2",
  background: "3"
});

export const DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE = DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.background;

export const DESTINATION_MANUAL_RUN_PROCESSOR_TYPE_OPTIONS = [
  { Text: "Real Time", Value: DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.realTime },
  { Text: "Async", Value: DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.async },
  { Text: "Azure Batch", Value: DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.background }
];

export const getValue = (item, ...keys) => {
  if (!item) return "";
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

export const getString = (item, ...keys) => String(getValue(item, ...keys) ?? "").trim();

export const getBoolean = (item, ...keys) => {
  const value = getValue(item, ...keys);
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = normalizeCompare(value);
  return normalized === "true" || normalized === "yes" || normalized === "1";
};

export const normalizeDestinationStateValue = (value, fallback = "1") => {
  const normalized = normalizeCompare(value);
  if (!normalized) return fallback;
  if (DESTINATION_STATE_LABELS[normalized]) return normalized;
  return DESTINATION_STATE_VALUES_BY_LABEL[normalized] || String(value);
};

export const getDestinationStateValueLabel = (value) => {
  const normalizedValue = normalizeDestinationStateValue(value, "");
  return DESTINATION_STATE_LABELS[normalizedValue] || String(value || "").trim();
};

export const normalizeSchemaDestinationStatusValue = (value, fallback = "0") => {
  const normalized = normalizeCompare(value);
  if (!normalized) return fallback;
  if (["0", "1", "2"].includes(normalized)) return normalized;
  return SCHEMA_DESTINATION_STATUS_VALUES_BY_LABEL[normalized] || fallback;
};

export const getSchemaDestinationStatusLabel = (value) => {
  const normalizedValue = normalizeSchemaDestinationStatusValue(value, "0");
  if (normalizedValue === "1") return "Online";
  if (normalizedValue === "2") return "Offline";
  return "Unavailable";
};

export const isSchemaDestinationOnline = (deployment) =>
  normalizeSchemaDestinationStatusValue(getValue(deployment, "SchemaDestinationStatus", "schemaDestinationStatus"), "0") === "1";

export const isSchemaDestinationConfigEditable = (deployment) =>
  normalizeSchemaDestinationStatusValue(getValue(deployment, "SchemaDestinationStatus", "schemaDestinationStatus"), "0") !== "1";

export const getEndpointDeploymentAuthModeValue = (deployment) => {
  const value = getValue(deployment, "ApiAuthenticationMode", "apiAuthenticationMode", "AuthenticationMode", "authenticationMode");
  const normalized = normalizeCompare(value);
  if (normalized === "basic") return "4";
  if (normalized === "pat + basic" || normalized === "pat+basic") return "5";
  if (normalized === "anonymous") return "3";
  if (normalized === "pat token" || normalized === "pat" || normalized === "bearer") return "1";
  return String(value || "").trim();
};

export const isEndpointDeploymentBasicOnly = (deployment) =>
  getEndpointDeploymentAuthModeValue(deployment) === "4";

export const isEndpointDeploymentMcpOnly = (deployment) => {
  const publicSurfaceMode = String(getValue(deployment, "PublicSurfaceMode", "publicSurfaceMode") || "").trim();
  if (publicSurfaceMode === "2") return true;
  if (publicSurfaceMode === "1" || publicSurfaceMode === "3") return false;

  const mcpEnabledValue = getValue(deployment, "McpEnabled", "mcpEnabled");
  const hostedApiEnabledValue = getValue(deployment, "HostedApiEnabled", "hostedApiEnabled");
  const mcpEnabled = mcpEnabledValue === true || normalizeCompare(mcpEnabledValue) === "true" || String(mcpEnabledValue || "") === "1";
  const hostedApiEnabled = hostedApiEnabledValue === true || normalizeCompare(hostedApiEnabledValue) === "true" || String(hostedApiEnabledValue || "") === "1";
  return mcpEnabled && !hostedApiEnabled;
};

export const normalizeFilePackagingModeValue = (value, fallback = "1") => {
  const normalized = normalizeCompare(value);
  if (!normalized) return fallback;
  if (FILE_PACKAGING_MODE_LABELS[normalized]) return normalized;
  return FILE_PACKAGING_MODE_VALUES_BY_LABEL[normalized] || String(value);
};

export const getFilePackagingModeLabel = (value) => {
  const normalizedValue = normalizeFilePackagingModeValue(value, "");
  return FILE_PACKAGING_MODE_LABELS[normalizedValue] || String(value || "").trim();
};

export const getArray = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

export const firstItem = (items) => {
  if (!items) return null;
  if (typeof items.first === "function") return items.first();
  return Array.isArray(items) ? items[0] : null;
};

export const getOperationResult = (response) => {
  const firstResponse = firstItem(response);
  return firstResponse?.Items || firstResponse?.Success !== undefined || firstResponse?.success !== undefined
    ? firstResponse
    : response;
};

export const getResultMessageText = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(getResultMessageText).filter(Boolean).join("\n");
  if (typeof value === "object") {
    const message = getString(value, "DisplayMessage", "displayMessage", "Content", "content", "Message", "message", "Text", "text", "Info", "info");
    if (message) return message;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value).trim();
};

const EMPTY_EVENT_ID = "00000000-0000-0000-0000-000000000000";
const REAL_TIME_PROCESSOR_TYPE = 1;

const isNoisyActionInProgressText = (value) =>
  String(value ?? "").trim().toLowerCase().startsWith("action in progress");

const getOperationEventId = (result) => ([
  getString(result, "EventId", "eventId", "EventID", "eventID"),
  getString(result, "CorrelationID", "correlationID", "CorrelationId", "correlationId"),
  getString(result?.CorrelationEvent, "_id", "EventId", "eventId"),
  getString(result?.Event, "_id", "EventId", "eventId"),
  getString(result?.Value, "EventId", "eventId", "EventID", "eventID", "CorrelationID", "correlationID", "CorrelationId", "correlationId")
].find((eventId) => eventId && eventId !== EMPTY_EVENT_ID) || "");

const getOperationProcessorType = (result) => {
  const processorType = [
    getValue(result?.CorrelationEvent, "ProcessorType", "processorType"),
    getValue(result?.Event, "ProcessorType", "processorType"),
    getValue(result, "ProcessorType", "processorType"),
    getValue(result?.Value, "ProcessorType", "processorType")
  ].map((value) => Number(value)).find((value) => Number.isFinite(value) && value > 0);
  return processorType || null;
};

const isActionInProgressResult = (result) => [
  getString(result?.Message, "Code", "code", "Name", "name"),
  getResultMessageText(getValue(result, "DisplayMessage", "displayMessage")),
  getResultMessageText(getValue(result?.Message, "DisplayMessage", "displayMessage", "Info", "info"))
].some((value) => {
  const normalized = String(value ?? "").trim();
  return normalized.toUpperCase() === "ACTION_IN_PROGRESS" || isNoisyActionInProgressText(normalized);
});

const getBackgroundOperationMessage = (result, baseMessage) => {
  const processorType = getOperationProcessorType(result);
  if (processorType === REAL_TIME_PROCESSOR_TYPE) {
    return "";
  }

  const isActionInProgress = isActionInProgressResult(result);
  const isBackground = getBoolean(result, "EventPersisted", "eventPersisted")
    || !!processorType
    || isActionInProgress;
  if (!isBackground) return "";

  const eventId = getOperationEventId(result);
  const backgroundMessage = eventId
    ? `Your action is being run in the background. <a href="?aiPanel=event&amp;aiPanelRoute=%2Fevent%2Fmanage%2F&amp;aiPanelId=${encodeURIComponent(eventId)}">Click here</a> to view and track the status of the event.`
    : "Action in progress: Event has been dispatched.";
  const normalizedBaseMessage = getResultMessageText(baseMessage);
  if (!normalizedBaseMessage || isNoisyActionInProgressText(normalizedBaseMessage)) {
    return backgroundMessage;
  }
  return `${normalizedBaseMessage}<br />${backgroundMessage}`;
};

export const getOperationResultMessage = (response, fallbackMessage) => {
  const result = getOperationResult(response);
  const resultMessage = getResultMessageText(getValue(result, "DisplayMessage", "displayMessage"))
    || getResultMessageText(getValue(result, "Message", "message"))
    || getResultMessageText(getValue(result, "Value", "value"))
    || fallbackMessage;
  return getBackgroundOperationMessage(result, resultMessage) || resultMessage;
};

export const getOperationResultDetails = (response) => {
  const result = getOperationResult(response);
  return [
    ...getArray(result, "MessageDetails", "messageDetails"),
    ...getArray(result, "Details", "details"),
    ...getArray(result, "Messages", "messages")
  ].map(getResultMessageText).filter(Boolean);
};

const collectResultObjectDetails = (result, label, details) => {
  if (!result || typeof result !== "object") return;
  const code = getString(result?.Message, "Code", "code") || getString(result, "Code", "code", "coreStatus");
  const displayMessage = getResultMessageText(getValue(result?.Message, "DisplayMessage", "displayMessage"))
    || getResultMessageText(getValue(result, "DisplayMessage", "displayMessage"));
  const messageInfo = getResultMessageText(getValue(result?.Message, "Info", "info"));
  const title = getResultMessageText(getValue(result, "Title", "title"));
  const detail = getResultMessageText(getValue(result, "Detail", "detail"));
  const type = getString(result, "Type", "type");
  const errorText = getResultMessageText(getValue(result, "Error", "error", "Errors", "errors"));
  const exception = getResultMessageText(getValue(result, "Exception", "exception", "ExceptionMessage", "exceptionMessage"));
  const stackTrace = getResultMessageText(getValue(result, "StackTrace", "stackTrace", "Stack", "stack"));
  const eventId = getString(result, "EventId", "eventId");
  const traceId = getString(result, "TraceId", "traceId", "RequestId", "requestId", "CorrelationId", "correlationId");

  if (label) details.push(label);
  if (code) details.push(`Code: ${code}`);
  if (title) details.push(`Title: ${title}`);
  if (type) details.push(`Type: ${type}`);
  if (displayMessage) details.push(`Message: ${displayMessage}`);
  if (detail) details.push(`Detail: ${detail}`);
  if (errorText) details.push(`Error: ${errorText}`);
  if (messageInfo) details.push(`Info: ${messageInfo}`);
  if (eventId && eventId !== "00000000-0000-0000-0000-000000000000") details.push(`EventId: ${eventId}`);
  if (traceId) details.push(`TraceId: ${traceId}`);
  if (exception) details.push(`Exception: ${exception}`);
  if (stackTrace) details.push(`StackTrace: ${stackTrace}`);
  details.push(...getOperationResultDetails(result));
  const validationErrors = getValue(result, "ValidationErrors", "validationErrors");
  if (validationErrors && typeof validationErrors === "object") {
    details.push(`ValidationErrors: ${getResultMessageText(validationErrors)}`);
  }
};

export const getOperationResultDebugDetails = (response) => {
  const details = [];
  const status = getValue(response, "status", "Status");
  const statusText = getString(response, "statusText", "StatusText");
  if (status || statusText) {
    details.push(`HTTP ${status || "status unknown"}${statusText ? ` ${statusText}` : ""}`);
  }

  const data = getValue(response, "data", "Data");
  if (Array.isArray(data)) {
    data.forEach((entry, index) => collectResultObjectDetails(entry, `Result ${index + 1}`, details));
  } else if (data && typeof data === "object") {
    collectResultObjectDetails(data, "Response data", details);
  } else if (data) {
    details.push(`Response data: ${getResultMessageText(data)}`);
  }

  collectResultObjectDetails(getOperationResult(response), "", details);

  if (details.length === 0) {
    const fallback = getResultMessageText(response);
    if (fallback) details.push(fallback);
  }

  return details.filter(Boolean).filter((detail, index, allDetails) =>
    allDetails.findIndex((candidate) => normalizeCompare(candidate) === normalizeCompare(detail)) === index
  );
};

export const getOperationItems = (response) => {
  const result = getOperationResult(response);
  return [
    ...getArray(result, "Items", "items"),
    ...getArray(response, "Items", "items")
  ].filter(Boolean);
};

export const getOperationItem = (response) => {
  const result = getOperationResult(response);
  return firstItem(result?.Items)
    || firstItem(result?.items)
    || result?.Item
    || result?.item
    || result?.Value
    || result?.value
    || null;
};

export const getId = (item) => getString(item, "_id", "Id", "id", "UniqueId", "uniqueId");

export const getDisplayName = (item, fallback = "") =>
  getString(item, "DisplayName", "displayName", "ResourceName", "resourceName", "ProductName", "productName", "Name", "name") || fallback;

export const getDestinationDeployments = (destination, defaults) =>
  getArray(
    destination,
    defaults?.deploymentCollection,
    "Deployments",
    "deployments",
    "WarehouseDeployments",
    "warehouseDeployments",
    "StorageDeployments",
    "storageDeployments",
    "ExternalSystemDeployments",
    "externalSystemDeployments",
    "SchemaAPIDeployments",
    "schemaAPIDeployments"
  );

export const getEndpointDeploymentSchemaScopes = (deployment) => {
  const configuredScopes = getArray(deployment, "SchemaScopes", "schemaScopes")
    .filter((scope) => getValue(scope, "IsDeleted", "isDeleted") !== true && getValue(scope, "IsActive", "isActive") !== false)
    .map((scope, index) => ({
      ...scope,
      SchemaScopeIdentifier: getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier"),
      __ScopeKey: getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier")
        || getString(scope, "SchemaVersionId", "schemaVersionId")
        || `scope-${index}`
    }))
    .filter((scope) => getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier"));

  if (configuredScopes.length) return configuredScopes;

  const fallbackIdentifier = getString(deployment, "SchemaScopeIdentifier", "schemaScopeIdentifier");
  if (!fallbackIdentifier) return [];
  return [{
    SchemaScopeIdentifier: fallbackIdentifier,
    SchemaId: getString(deployment, "SchemaId", "schemaId"),
    SchemaName: getString(deployment, "SchemaName", "schemaName"),
    SchemaVersionId: getString(deployment, "CurrentSchemaVersionId", "currentSchemaVersionId", "SchemaVersionId", "schemaVersionId"),
    SchemaVersion: getString(deployment, "CurrentSchemaVersion", "currentSchemaVersion"),
    __ScopeKey: fallbackIdentifier
  }];
};

export const getDestinationEntityDeployments = (destination) =>
  getArray(destination, "EntityObjectDeployments", "entityObjectDeployments");

export const getDestinationSchemaId = (destination) =>
  getString(destination, "SchemaId", "schemaId");

export const getDestinationActiveSchemaVersionId = (destination) =>
  getString(destination, "ActiveSchemaVersionId", "activeSchemaVersionId", "SchemaVersionId", "schemaVersionId");

export const getSchemaVersionLabel = (schema, schemaVersionId) => {
  const versions = getArray(schema, "SchemaVersions", "schemaVersions");
  const version = versions.find((candidate) =>
    normalizeCompare(getString(candidate, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId")) === normalizeCompare(schemaVersionId)
  );
  return getString(version, "Version", "version", "DisplayName", "displayName")
    || getString(version, "Title", "title")
    || schemaVersionId
    || "Not set";
};

export const getDeploymentId = (deployment) =>
  getString(deployment, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId", "SchemaAPIDeploymentId", "schemaAPIDeploymentId", "schemaApiDeploymentId", "WarehouseDeploymentId", "warehouseDeploymentId", "StorageDeploymentId", "storageDeploymentId", "ExternalSystemDeploymentId", "externalSystemDeploymentId", "DeploymentId", "deploymentId", "UniqueId", "uniqueId", "_id", "id");

export const getDeploymentTargetFieldName = (defaults) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  if (managerKey === "warehouse") return "DBName";
  if (managerKey === "externalsystem") return "TargetName";
  if (managerKey === "schemaapi") return "Key";
  return "RootPath";
};

export const getDeploymentTargetDisplayName = (defaults) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  if (managerKey === "warehouse") return "Database Name";
  if (managerKey === "externalsystem") return "Target Name";
  if (managerKey === "schemaapi") return "Deployment Key";
  return "Root Path / Container";
};

export const getDeploymentTargetDescription = (defaults) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  if (managerKey === "warehouse") return "Database, schema, or warehouse target used for this deployment.";
  if (managerKey === "externalsystem") return "External system target used for this deployment.";
  if (managerKey === "schemaapi") return "Route-safe deployment key used in hosted API, OpenAPI, and MCP URLs.";
  return "Remote folder, container, or share for this deployment.";
};

export const getDeploymentStateFieldName = (defaults) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  if (managerKey === "warehouse") return "WarehouseState";
  if (managerKey === "externalsystem") return "ExternalSystemState";
  if (managerKey === "schemaapi") return "";
  return "StorageState";
};

export const getDeploymentStateDisplayName = (defaults) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  if (managerKey === "warehouse") return "Warehouse State";
  if (managerKey === "externalsystem") return "External System State";
  if (managerKey === "schemaapi") return "Status";
  return "Storage State";
};

export const getDeploymentTargetValue = (deployment, defaults) => {
  const targetFieldName = getDeploymentTargetFieldName(defaults);
  const value = getString(deployment, targetFieldName, targetFieldName.charAt(0).toLowerCase() + targetFieldName.slice(1));
  if (normalizeCompare(defaults?.managerKey) === "schemaapi") {
    return value || getString(deployment, "SystemName", "systemName", "DeploymentName", "deploymentName", "DeploymentId", "deploymentId", "UniqueId", "uniqueId");
  }
  return value || getString(deployment, "DBName", "dbName", "RootPath", "rootPath", "RootContainer", "rootContainer", "TargetName", "targetName");
};

export const getDeploymentDefaultTargetValue = (destination, defaults) => {
  const targetFieldName = getDeploymentTargetFieldName(defaults);
  if (targetFieldName === "DBName") {
    return getString(destination, "DefaultDBName", "defaultDBName", "DBName", "dbName", "DefaultRootPath", "defaultRootPath") || "/";
  }
  if (targetFieldName === "TargetName") {
    return getString(destination, "DefaultTargetName", "defaultTargetName", "TargetName", "targetName", "DefaultRootPath", "defaultRootPath") || "/";
  }
  if (targetFieldName === "Key") {
    return getString(destination, "APIName", "apiName", "SystemName", "systemName") || "default";
  }
  return getString(destination, "DefaultRootPath", "defaultRootPath", "RootPath", "rootPath", "RootContainer", "rootContainer") || "/";
};

export const getDeploymentPairKey = (deployment, defaults) => [
  getString(deployment, "SchemaDestinationId", "schemaDestinationId"),
  getString(deployment, "SchemaVersionId", "schemaVersionId"),
  getDeploymentTargetValue(deployment, defaults),
  getDisplayName(deployment)
].map(normalizeCompare).filter(Boolean).join(":");

export const getDeploymentMatchKey = (deployment, defaults) => {
  const id = getDeploymentId(deployment);
  if (id) return `id:${normalizeCompare(id)}`;
  const clientKey = getString(deployment, "__ClientKey", "_tempKey");
  if (clientKey) return `client:${normalizeCompare(clientKey)}`;
  const pairKey = getDeploymentPairKey(deployment, defaults);
  return pairKey ? `pair:${pairKey}` : "";
};

export const getDeploymentRenderKey = (deployment, defaults, index = 0) =>
  getDeploymentId(deployment)
  || getString(deployment, "__ClientKey", "_tempKey")
  || getDeploymentPairKey(deployment, defaults)
  || `deployment-${index}`;

export const findDestinationDeploymentById = (destination, defaults, deploymentId) => {
  const normalizedDeploymentId = normalizeCompare(deploymentId);
  if (!normalizedDeploymentId) return null;
  return getDestinationDeployments(destination, defaults).find((deployment, index) =>
    normalizeCompare(getDeploymentId(deployment)) === normalizedDeploymentId
    || normalizeCompare(getDeploymentRenderKey(deployment, defaults, index)) === normalizedDeploymentId
    || normalizeCompare(getDeploymentMatchKey(deployment, defaults)) === normalizedDeploymentId
  ) || null;
};

export const DESTINATION_MANUAL_RUN_OPERATIONS = {
  warehouse: Operations.WarehouseItem?.FullWarehouseLoadProcess || "FullWarehouseLoadProcess",
  fileStorage: Operations.FileStorageItem?.FileStorageSync || "FileStorageSync",
  externalSystem: {
    sync: Operations.ExternalSystemItem?.ExternalSystemSync || "ExternalSystemSync",
    validate: Operations.ExternalSystemItem?.ExternalSystemValidate || "ExternalSystemValidate",
    preview: Operations.ExternalSystemItem?.ExternalSystemPreview || "ExternalSystemPreview",
    discoverDirty: Operations.ExternalSystemItem?.ExternalSystemDiscoverDirty || "ExternalSystemDiscoverDirty",
    loadDirtyQueue: Operations.ExternalSystemItem?.ExternalSystemLoadDirtyQueue || "ExternalSystemLoadDirtyQueue"
  }
};

export const DESTINATION_WAREHOUSE_DEPLOYMENT_OPERATIONS = {
  deploy: Operations.WarehouseItem?.WarehouseDeploy || "WarehouseDeploy",
  drop: Operations.WarehouseItem?.WarehouseDrop || "WarehouseDrop",
  clone: Operations.WarehouseItem?.CloneWarehouseDeployment || "CloneWarehouseDeployment"
};

export const DESTINATION_MANUAL_RUN_EXTERNAL_MODE_OPTIONS = [
  { Text: "Sync", Value: "sync" },
  { Text: "Validate", Value: "validate" },
  { Text: "Preview", Value: "preview" },
  { Text: "Discover Dirty", Value: "discoverDirty" },
  { Text: "Load Dirty Queue", Value: "loadDirtyQueue" }
];

export const DESTINATION_MANUAL_RUN_AUDIENCE = {
  normal: "normal",
  admin: "admin"
};

export const DESTINATION_MANUAL_RUN_WAREHOUSE_MODES = [
  {
    key: "incremental-load",
    label: "Incremental Load",
    description: "Load changed records, refresh source keys, and run post-load scripts for the selected entity objects.",
    processorStep: "PartialWarehouseSync",
    impact: "Standard",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal,
    supportsForceReconcile: true,
    supportsParallelProcessing: true
  },
  {
    key: "full-reload",
    label: "Full Reload",
    description: "Reload all source records for the selected entity objects, refresh keys, and run post-load scripts.",
    processorStep: "FullWarehouseSync",
    impact: "High impact",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal,
    supportsForceReconcile: true,
    supportsParallelProcessing: true
  },
  {
    key: "refresh-source-keys",
    label: "Refresh Source Keys Only",
    description: "Refresh source object key tracking without loading warehouse tables.",
    processorStep: "SourceObjectKeySync",
    impact: "Keys only",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal,
    supportsParallelProcessing: true
  },
  {
    key: "incremental-no-keys",
    label: "Incremental Load Without Key Refresh",
    description: "Run an incremental warehouse load using existing source key state.",
    processorStep: "PartialWarehouseSyncNoKeys",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsForceReconcile: true,
    supportsParallelProcessing: true
  },
  {
    key: "incremental-no-post-load",
    label: "Incremental Load Without Post-Load Scripts",
    description: "Run an incremental warehouse load and skip post-load scripts.",
    processorStep: "PartialWarehouseSyncNPL",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsForceReconcile: true,
    supportsParallelProcessing: true
  },
  {
    key: "full-no-keys",
    label: "Full Reload Without Key Refresh",
    description: "Run a full reload using existing source key state.",
    processorStep: "FullWarehouseSyncNoKeys",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsForceReconcile: true,
    supportsParallelProcessing: true
  },
  {
    key: "full-no-post-load",
    label: "Full Reload Without Post-Load Scripts",
    description: "Run a full reload and skip post-load scripts.",
    processorStep: "FullWarehouseSyncNPL",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsForceReconcile: true,
    supportsParallelProcessing: true
  },
  {
    key: "reload-source-keys",
    label: "Reload Source Keys",
    description: "Rebuild source object key tracking from source data.",
    processorStep: "SourceObjectKeySyncWithReload",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsParallelProcessing: true
  },
  {
    key: "entity-master-partial",
    label: "Refresh Entity Master Records",
    description: "Rebuild entity master records for changed source records.",
    processorStep: "EntityMasterRecordPartialSync",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsParallelProcessing: true
  },
  {
    key: "entity-master-full",
    label: "Full Refresh Entity Master Records",
    description: "Rebuild entity master records from the full selected source scope.",
    processorStep: "EntityMasterRecordFullSync",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsParallelProcessing: true
  },
  {
    key: "post-load-only",
    label: "Post-Load Scripts Only",
    description: "Run post-load scripts without refreshing source keys or loading warehouse tables.",
    processorStep: "PostLoadOnly",
    impact: "Advanced",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsParallelProcessing: true
  },
  {
    key: "reset-cursors-only",
    label: "Reset Cursors Only",
    description: "Move selected entity cursors back to a date or identifier without loading warehouse data.",
    processorStep: "ResetOnly",
    impact: "Maintenance",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsResetCursor: true
  },
  {
    key: "update-cursors",
    label: "Update Deployment Cursors",
    description: "Update stored cursor date and identifier values for selected entity deployments.",
    processorStep: "UpdateEntityDateInternalId",
    impact: "Maintenance",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.admin,
    supportsParallelProcessing: true
  }
];

export const DESTINATION_MANUAL_RUN_FILE_STORAGE_MODES = [
  {
    key: "incremental-file-sync",
    label: "Incremental File Sync",
    description: "Create destination files for changed records in the selected entity objects.",
    processorStep: "incremental",
    impact: "Standard",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal
  },
  {
    key: "full-file-sync",
    label: "Full File Sync",
    description: "Rebuild destination files for the selected entity objects.",
    processorStep: "full",
    impact: "High impact",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal
  }
];

export const DESTINATION_MANUAL_RUN_EXTERNAL_MODES = [
  {
    key: "sync",
    label: "Sync",
    description: "Sync selected entity objects to the external system.",
    processorStep: "sync",
    impact: "Standard",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal,
    supportsRecordLimit: true
  },
  {
    key: "validate",
    label: "Validate",
    description: "Validate external system access and setup without syncing records.",
    processorStep: "validate",
    impact: "Validation",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal
  },
  {
    key: "preview",
    label: "Preview",
    description: "Preview records that would be sent to the external system.",
    processorStep: "preview",
    impact: "Preview",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal,
    supportsRecordLimit: true,
    defaultTake: "25"
  },
  {
    key: "discoverDirty",
    label: "Discover Dirty Records",
    description: "Find records that need external-system synchronization.",
    processorStep: "discoverDirty",
    impact: "Discovery",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal
  },
  {
    key: "loadDirtyQueue",
    label: "Load Dirty Queue",
    description: "Load discovered dirty records into the external-system sync queue.",
    processorStep: "loadDirtyQueue",
    impact: "Queue",
    audience: DESTINATION_MANUAL_RUN_AUDIENCE.normal,
    supportsRecordLimit: true
  }
];

export const DESTINATION_MANUAL_RUN_WAREHOUSE_STEP_OPTIONS = [
  { Text: "Set Deployment Dates/Ids (Only)", Value: "ResetOnly" },
  { Text: "Update Deployment Dates/Ids Records (Only)", Value: "UpdateEntityDateInternalId" },
  { Text: "Post Load (Only)", Value: "PostLoadOnly" },
  { Text: "Source Object Key Sync (Partial)", Value: "SourceObjectKeySync" },
  { Text: "Source Object Key Sync (Full)", Value: "SourceObjectKeySyncWithReload" },
  { Text: "Entity Master Sync (Partial)", Value: "EntityMasterRecordPartialSync" },
  { Text: "Entity Master Sync (Full)", Value: "EntityMasterRecordFullSync" },
  { Text: "Partial Warehouse Sync (Skip Keys)", Value: "PartialWarehouseSyncNoKeys" },
  { Text: "Partial Warehouse Sync (Skip Post Load)", Value: "PartialWarehouseSyncNPL" },
  { Text: "Partial Warehouse Sync", Value: "PartialWarehouseSync" },
  { Text: "Full Warehouse Sync (Skip Keys)", Value: "FullWarehouseSyncNoKeys" },
  { Text: "Full Warehouse Sync (Skip Post Load)", Value: "FullWarehouseSyncNPL" },
  { Text: "Full Warehouse Sync", Value: "FullWarehouseSync" }
];

export const getDestinationManualRunModeDefinitions = (defaults, isSystemAdmin = false) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  const modes = managerKey === "warehouse"
    ? DESTINATION_MANUAL_RUN_WAREHOUSE_MODES
    : managerKey === "externalsystem"
      ? DESTINATION_MANUAL_RUN_EXTERNAL_MODES
      : managerKey === "filestorage"
        ? DESTINATION_MANUAL_RUN_FILE_STORAGE_MODES
        : [];
  return modes.filter((mode) => mode.audience !== DESTINATION_MANUAL_RUN_AUDIENCE.admin || isSystemAdmin);
};

export const getDestinationManualRunSelectedMode = (defaults, values = {}, isSystemAdmin = false) => {
  const modes = getDestinationManualRunModeDefinitions(defaults, isSystemAdmin);
  const managerKey = normalizeCompare(defaults?.managerKey);
  const requested = managerKey === "warehouse"
    ? modes.find((mode) => mode.processorStep === values.step || mode.key === values.runMode)
    : managerKey === "externalsystem"
      ? modes.find((mode) => mode.processorStep === values.externalSystemOperation || mode.key === values.runMode)
      : managerKey === "filestorage"
        ? modes.find((mode) => mode.key === values.runMode || (values.isfullsync ? mode.key === "full-file-sync" : mode.key === "incremental-file-sync"))
        : null;
  return requested || modes[0] || null;
};

export const normalizeDestinationManualRunProcessorType = (value) => {
  const normalizedValue = String(value ?? "").trim();
  return DESTINATION_MANUAL_RUN_PROCESSOR_TYPE_OPTIONS.some((option) => option.Value === normalizedValue)
    ? normalizedValue
    : DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE;
};

export const normalizeDestinationManualRunValues = (defaults, values = {}, selectedMode = null) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  const next = {
    ...(values || {}),
    eoids: normalizeDestinationManualRunEntityIds(values.eoids),
    processorType: normalizeDestinationManualRunProcessorType(values.processorType)
  };
  const mode = selectedMode || getDestinationManualRunSelectedMode(defaults, next, true);

  if (managerKey === "warehouse") {
    next.runMode = mode?.key || "incremental-load";
    next.step = mode?.processorStep || "PartialWarehouseSync";
    if (!mode?.supportsResetCursor) {
      next.resetfromdate = "";
      next.resetfromvalue = "";
    }
    if (!mode?.supportsForceReconcile) next.fr = false;
    if (!mode?.supportsParallelProcessing) next.multiThread = false;
    if (!mode?.supportsFirstRunReset) next.force = false;
  } else if (managerKey === "externalsystem") {
    next.runMode = mode?.key || "sync";
    next.externalSystemOperation = mode?.processorStep || "sync";
    if (mode?.supportsRecordLimit) {
      next.take = String(next.take || mode.defaultTake || "250");
    } else {
      next.take = "";
    }
  } else if (managerKey === "filestorage") {
    next.runMode = mode?.key || "incremental-file-sync";
    next.isfullsync = mode?.key === "full-file-sync";
  }

  return next;
};

export const validateDestinationManualRunValues = (defaults, values = {}, selectedMode = null) => {
  const mode = selectedMode || getDestinationManualRunSelectedMode(defaults, values, true);
  const entityIds = normalizeDestinationManualRunEntityIds(values.eoids);
  if (!entityIds.length) return "Select at least one Entity Object before running.";
  if (mode?.supportsResetCursor && !String(values.resetfromdate || "").trim() && !String(values.resetfromvalue || "").trim()) {
    return "Enter a reset date or reset identifier before running Reset Cursors Only.";
  }
  return "";
};

export const getEntityDeploymentId = (entityDeployment) =>
  getString(entityDeployment, "EntityObjectDeploymentId", "entityObjectDeploymentId", "UniqueId", "uniqueId", "_id", "id");

export const getEntityDeploymentPairKey = (entityDeployment) => [
  getString(entityDeployment, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId", "DeploymentId", "deploymentId"),
  getString(entityDeployment, "EntityObjectId", "entityObjectId")
].map(normalizeCompare).filter(Boolean).join(":");

export const getEntityDeploymentMatchKey = (entityDeployment) => {
  const id = getEntityDeploymentId(entityDeployment);
  return id ? `id:${normalizeCompare(id)}` : `pair:${getEntityDeploymentPairKey(entityDeployment)}`;
};

export const getEntityDeploymentRenderKey = (entityDeployment, index = 0) =>
  getEntityDeploymentId(entityDeployment)
  || getString(entityDeployment, "__ClientKey", "_tempKey")
  || getEntityDeploymentPairKey(entityDeployment)
  || `entity-deployment-${index}`;

export const getDeploymentRootName = (deployment, defaults = null) => {
  const root = getDeploymentTargetValue(deployment, defaults);
  return root === "/" ? "Root" : root;
};

export const getDeploymentDisplayName = (deployment, destination, schemaVersionLabel = "") =>
  getDisplayName(deployment)
  || [
    getDisplayName(destination, getString(deployment, "SchemaDestinationName", "schemaDestinationName") || "Deployment"),
    getDeploymentRootName(deployment, null),
    schemaVersionLabel
  ].filter(Boolean).join(" - ")
  || "Deployment";

export const buildDestinationManualRunField = ({
  fieldName,
  displayName,
  fieldType = "TextBox",
  options = [],
  description = "",
  isRequired = false
}) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: fieldType,
  Description: description,
  IsRequired: isRequired,
  DataJson: options
});

export const getDestinationManualRunInitialValues = (defaults) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  if (managerKey === "schemaapi") return {};
  if (managerKey === "warehouse") {
    return {
      eoids: [],
      runMode: "incremental-load",
      step: "PartialWarehouseSync",
      resetfromdate: "",
      resetfromvalue: "",
      fr: false,
      multiThread: false,
      force: false,
      processorType: DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE
    };
  }
  if (managerKey === "externalsystem") {
    return {
      eoids: [],
      runMode: "sync",
      externalSystemOperation: "sync",
      take: "250",
      processorType: DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE
    };
  }
  return {
    eoids: [],
    runMode: "incremental-file-sync",
    isfullsync: false,
    debug: true,
    processorType: DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE
  };
};

export const normalizeDestinationManualRunEntityIds = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(getValue(entry, "Value", "value", "Text", "text") || entry || "").trim()).filter(Boolean);
  }
  const text = String(value ?? "").trim();
  if (!text) return [];
  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return normalizeDestinationManualRunEntityIds(parsed);
    } catch {
      return [];
    }
  }
  return text.split(",").map((entry) => entry.trim()).filter(Boolean);
};

const getEntityObjectId = (entityObject) =>
  getString(entityObject, "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId", "_id", "id");

const getEntityDeploymentTargetDeploymentId = (entityDeployment) =>
  getString(
    entityDeployment,
    "SchemaDestinationDeploymentId",
    "schemaDestinationDeploymentId",
    "WarehouseDeploymentId",
    "warehouseDeploymentId",
    "StorageDeploymentId",
    "storageDeploymentId",
    "ExternalSystemDeploymentId",
    "externalSystemDeploymentId",
    "DeploymentId",
    "deploymentId"
  );

const getEntityDeploymentEntityObjectId = (entityDeployment) =>
  getString(entityDeployment, "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId", "_id", "id");

export const getDestinationManualRunEntityOptions = (entityObjects = [], deployment = null, destination = null) => {
  const deploymentId = getDeploymentId(deployment);
  const normalizedDeploymentId = normalizeCompare(deploymentId);
  const scopedEntityDeployments = normalizedDeploymentId
    ? getDestinationEntityDeployments(destination)
      .filter((entityDeployment) =>
        !isRecordDeleted(entityDeployment)
        && isRecordActive(entityDeployment)
        && normalizeCompare(getEntityDeploymentTargetDeploymentId(entityDeployment)) === normalizedDeploymentId
        && getEntityDeploymentEntityObjectId(entityDeployment)
      )
    : [];
  const scopedEntityIds = normalizedDeploymentId
    ? new Set(scopedEntityDeployments.map((entityDeployment) => normalizeCompare(getEntityDeploymentEntityObjectId(entityDeployment))))
    : null;

  const options = getArray({ entityObjects }, "entityObjects")
    .filter((entityObject) => !isRecordDeleted(entityObject) && isRecordActive(entityObject))
    .filter((entityObject) => !scopedEntityIds || scopedEntityIds.has(normalizeCompare(getEntityObjectId(entityObject))))
    .map((entityObject) => {
      const value = getEntityObjectId(entityObject) || getId(entityObject);
      return {
        Text: getDisplayName(entityObject, getString(entityObject, "ObjectName", "objectName") || value || "Entity Object"),
        Value: value
      };
    })
    .filter((option) => option.Value);

  if (!scopedEntityIds) return options;

  const optionIds = new Set(options.map((option) => normalizeCompare(option.Value)));
  scopedEntityDeployments.forEach((entityDeployment) => {
    const value = getEntityDeploymentEntityObjectId(entityDeployment);
    const normalizedValue = normalizeCompare(value);
    if (!value || optionIds.has(normalizedValue)) return;
    options.push({
      Text: getString(entityDeployment, "EntityObjectName", "entityObjectName", "ObjectName", "objectName", "DisplayName", "displayName") || value,
      Value: value
    });
    optionIds.add(normalizedValue);
  });

  return options;
};

export const getDestinationManualRunOperationName = (defaults, values = {}) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  if (managerKey === "schemaapi") return "";
  if (managerKey === "warehouse") return DESTINATION_MANUAL_RUN_OPERATIONS.warehouse;
  if (managerKey === "externalsystem") {
    const selectedMode = getDestinationManualRunSelectedMode(defaults, values, true);
    return DESTINATION_MANUAL_RUN_OPERATIONS.externalSystem[selectedMode?.processorStep || values.externalSystemOperation || "sync"]
      || DESTINATION_MANUAL_RUN_OPERATIONS.externalSystem.sync;
  }
  return DESTINATION_MANUAL_RUN_OPERATIONS.fileStorage;
};

export const buildDestinationManualRunMetadata = ({ defaults, deployment, values = {} }) => {
  const deploymentId = getDeploymentId(deployment);
  const managerKey = normalizeCompare(defaults?.managerKey);
  const selectedMode = getDestinationManualRunSelectedMode(defaults, values, true);
  const normalizedValues = normalizeDestinationManualRunValues(defaults, values, selectedMode);
  const eoids = normalizeDestinationManualRunEntityIds(normalizedValues.eoids).join(",");
  if (managerKey === "schemaapi") {
    return {
      DeploymentId: deploymentId,
      SchemaDestinationDeploymentId: deploymentId
    };
  }
  if (managerKey === "warehouse") {
    return {
      wdid: deploymentId,
      eoids,
      step: normalizedValues.step || "PartialWarehouseSync",
      resetfromdate: normalizedValues.resetfromdate || "",
      resetfromvalue: normalizedValues.resetfromvalue || "",
      fr: normalizedValues.fr ? "true" : "false",
      multiThread: normalizedValues.multiThread ? "true" : "false",
      force: normalizedValues.force ? "true" : "false"
    };
  }
  if (managerKey === "externalsystem") {
    return {
      esdid: deploymentId,
      eoids,
      take: normalizedValues.take || (normalizedValues.externalSystemOperation === "preview" ? "25" : "250")
    };
  }
  return {
    fsdid: deploymentId,
    eoids,
    isfullsync: normalizedValues.isfullsync ? "true" : "false",
    debug: normalizedValues.debug ? "true" : "false"
  };
};

export const buildWarehouseDeployMetadata = ({ deployment, values = {} }) => ({
  wdid: getDeploymentId(deployment),
  eoids: normalizeDestinationManualRunEntityIds(values.eoids).join(","),
  includeViews: values.includeViews === true ? "true" : "false",
  as: values.as === true ? "true" : "false"
});

export const buildWarehouseDropMetadata = ({ deployment }) => ({
  wdid: getDeploymentId(deployment)
});

export const buildWarehouseCloneMetadata = ({ deployment, values = {} }) => {
  const metadata = {
    wdid: getDeploymentId(deployment),
    dbn: String(values.dbn || "").trim()
  };
  const warehouseName = String(values.whn || "").trim();
  const schemaVersionId = String(values.svid || "").trim();
  if (warehouseName) metadata.whn = warehouseName;
  if (schemaVersionId) metadata.svid = schemaVersionId;
  return metadata;
};

export const validateWarehouseDeployValues = (values = {}) =>
  normalizeDestinationManualRunEntityIds(values.eoids).length || values.includeViews === true
    ? ""
    : "Select at least one Entity Object or enable Deploy Warehouse Views before deploying.";

export const getWarehouseSchemaVersionOptions = (schema = null) =>
  getArray(schema, "SchemaVersions", "schemaVersions")
    .map((version) => {
      const value = getString(version, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId", "_id", "id");
      const label = getString(version, "Title", "title", "DisplayName", "displayName")
        || getString(version, "Version", "version")
        || value;
      const versionNumber = getString(version, "Version", "version");
      return value
        ? {
          Text: versionNumber && !normalizeCompare(label).includes(normalizeCompare(versionNumber))
            ? `${label} (${versionNumber})`
            : label,
          Value: value
        }
        : null;
    })
    .filter(Boolean);

export const getDestinationStateLabel = (item) =>
  getDestinationStateValueLabel(getString(item, "DestinationState", "destinationState", "WarehouseState", "warehouseState", "StorageState", "storageState", "ExternalSystemState", "externalSystemState"))
  || getString(item, "SchemaDestinationStatus", "schemaDestinationStatus")
  || "Not set";

export const getDestinationConnectionSummary = (destination) =>
  getString(destination, "DefaultConnectionString", "defaultConnectionString", "ConnectionString", "connectionString")
    ? "Configured"
    : "Not set";

export const buildDestinationDeploymentDraft = (destination, defaults, schemaVersionLabel = "") => {
  const destinationName = getDisplayName(destination, "Destination");
  const systemName = getString(destination, "SystemName", "systemName") || destinationName;
  const targetFieldName = getDeploymentTargetFieldName(defaults);
  const stateFieldName = getDeploymentStateFieldName(defaults);
  const root = getDeploymentDefaultTargetValue(destination, defaults) || "/";
  const schemaVersionId = getDestinationActiveSchemaVersionId(destination);
  destinationDeploymentClientKeySequence += 1;
  const clientKey = [
    getId(destination),
    targetFieldName,
    root,
    schemaVersionId,
    destinationDeploymentClientKeySequence
  ].map(normalizeCompare).filter(Boolean).join(":");
  const draft = {
    __ClientKey: clientKey ? `destination-deployment:${clientKey}` : "",
    [targetFieldName]: root,
    ...(stateFieldName ? { [stateFieldName]: "1" } : {}),
    DisplayName: getDeploymentDisplayName({ [targetFieldName]: root }, destination, schemaVersionLabel)
  };
  return {
    ...draft,
    IsNew: true,
    IsActive: true,
    IsDeleted: false,
    SchemaDestinationId: getId(destination),
    SchemaDestinationName: destinationName,
    SchemaDestinationSystemName: systemName,
    SchemaDestinationStatus: "0",
    SchemaVersionId: schemaVersionId,
    CurrentSchemaVersionId: schemaVersionId,
    MaxConcurrency: 9999,
    ...(defaults?.isEndpoint
      ? {
        PublicSurfaceMode: getValue(defaults, "defaultPublicSurfaceModeValue", "DefaultPublicSurfaceModeValue") || 3,
        HostingMode: 1,
        WarehouseNativeQueryMode: 0,
        McpExecutionIdentityMode: 1
      }
      : { EnableSync: false }),
    DestinationSystemType: getValue(destination, "DestinationSystemType", "destinationSystemType"),
    WarehouseSystemType: getValue(destination, "WarehouseSystemType", "warehouseSystemType"),
    StorageSystemType: getValue(destination, "StorageSystemType", "storageSystemType"),
    ExternalSystemType: getValue(destination, "ExternalSystemType", "externalSystemType")
  };
};

export const buildDestinationEntityDeploymentDraft = (entityObject, deployment, destination) => {
  const entityObjectId = getString(entityObject, "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId", "_id", "id");
  const deploymentId = getDeploymentId(deployment);
  const clientKey = [getId(destination), deploymentId, entityObjectId].map(normalizeCompare).filter(Boolean).join(":");
  return {
    __ClientKey: clientKey ? `entity-deployment:${clientKey}` : "",
    IsNew: true,
    IsActive: true,
    IsDeleted: false,
    EntityObjectId: entityObjectId,
    EntityDisplayName: getDisplayName(entityObject, getString(entityObject, "ObjectName", "objectName") || "Entity Object"),
    EntityObjectName: getString(entityObject, "ObjectName", "objectName"),
    SchemaDestinationId: getId(destination),
    SchemaDestinationName: getDisplayName(deployment, getDisplayName(destination, "Destination")),
    SchemaDestinationDeploymentId: deploymentId,
    DestinationState: "1",
    DisableTruncate: true,
    ForceReconcile: false,
    FilePackagingMode: "1"
  };
};

const stripDestinationClientOnlyFields = (row) => {
  const cleanRow = { ...(row || {}) };
  delete cleanRow.__ClientKey;
  delete cleanRow._tempKey;
  delete cleanRow.DeploymentId;
  delete cleanRow.DeploymentName;
  delete cleanRow.Status;
  delete cleanRow.Text;
  delete cleanRow.Value;
  delete cleanRow.Sequence;
  delete cleanRow.Disabled;
  delete cleanRow.Error;
  delete cleanRow.Important;
  if (cleanRow.SchemaDestinationStatus !== undefined || cleanRow.schemaDestinationStatus !== undefined) {
    cleanRow.SchemaDestinationStatus = normalizeSchemaDestinationStatusValue(
      getValue(cleanRow, "SchemaDestinationStatus", "schemaDestinationStatus")
    );
    delete cleanRow.schemaDestinationStatus;
  }
  ["StorageState", "WarehouseState", "ExternalSystemState", "DestinationState"].forEach((fieldName) => {
    const lowerFieldName = lowerFirst(fieldName);
    if (cleanRow[fieldName] !== undefined || cleanRow[lowerFieldName] !== undefined) {
      cleanRow[fieldName] = normalizeDestinationStateValue(getValue(cleanRow, fieldName, lowerFieldName), "0");
      delete cleanRow[lowerFieldName];
    }
  });
  if (cleanRow.FilePackagingMode !== undefined || cleanRow.filePackagingMode !== undefined) {
    cleanRow.FilePackagingMode = normalizeFilePackagingModeValue(getValue(cleanRow, "FilePackagingMode", "filePackagingMode"));
    delete cleanRow.filePackagingMode;
  }
  return cleanRow;
};

const lowerFirst = (value) =>
  value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : value;

export const getDestinationDeploymentCollectionFieldNames = (defaults = {}) =>
  Array.from(new Set([
    defaults?.deploymentCollection,
    lowerFirst(defaults?.deploymentCollection || ""),
    "Deployments",
    "deployments",
    "WarehouseDeployments",
    "warehouseDeployments",
    "StorageDeployments",
    "storageDeployments",
    "StorageDeploymentsAlias",
    "storageDeploymentsAlias",
    "ExternalSystemDeployments",
    "externalSystemDeployments"
  ].filter(Boolean)));

export const normalizeDestinationDeploymentRowsForSave = (deployments = []) =>
  getArray({ deployments }, "deployments").map(stripDestinationClientOnlyFields);

export const updateDestinationDeploymentCollection = (destination, defaults, deployments) => {
  const nextDestination = {
    ...(destination || {}),
    IsNew: false
  };
  getDestinationDeploymentCollectionFieldNames(defaults).forEach((fieldName) => {
    delete nextDestination[fieldName];
  });
  return {
    ...nextDestination,
    Deployments: normalizeDestinationDeploymentRowsForSave(deployments)
  };
};

export const normalizeDestinationSavePayload = (destination, defaults) =>
  updateDestinationDeploymentCollection(
    destination,
    defaults,
    getDestinationDeployments(destination, defaults)
  );

export const updateDestinationEntityDeploymentCollection = (destination, entityDeployments, defaults = null) => ({
  ...(defaults ? normalizeDestinationSavePayload(destination, defaults) : { ...(destination || {}), IsNew: false }),
  EntityObjectDeployments: getArray({ entityDeployments }, "entityDeployments").map(stripDestinationClientOnlyFields)
});

export const getDestinationResourceDefaults = (productResource) => {
  const productKey = normalizeCompare(getString(productResource, "ProductKey", "productKey"));
  const resourceKey = normalizeCompare(getString(productResource, "ProductResourceKey", "productResourceKey"));
  return Object.values(DESTINATION_RESOURCE_DEFAULTS).find((defaults) =>
    defaults.productKeys.some((key) => normalizeCompare(key) === productKey)
    || defaults.resourceKeys.some((key) => normalizeCompare(key) === resourceKey)
  ) || null;
};

export const isDestinationResource = (productResource) => Boolean(getDestinationResourceDefaults(productResource));

export const supportsDestinationSchedules = (productResource) => {
  const defaults = getDestinationResourceDefaults(productResource);
  return Boolean(defaults) && defaults.supportsSchedules !== false;
};

export const isDestinationLinkedRecord = (record) => {
  const itemName = normalizeCompare(getString(record, "ItemName", "itemName"));
  const relationship = normalizeCompare(getString(record, "Relationship", "relationship"));
  return DESTINATION_LINKED_ITEM_NAMES.has(itemName)
    && (relationship === "schemadestination" || relationship === "schema destination");
};

export const getLinkedDestinationRecord = (configuration) =>
  getArray(configuration, "LinkedRecords", "linkedRecords").find(isDestinationLinkedRecord) || null;

export const getDestinationDefaultsFromRecord = (record, productResource = null) => {
  const itemName = normalizeCompare(getString(record, "ItemName", "itemName"));
  if (itemName === "warehouse") return DESTINATION_RESOURCE_DEFAULTS.warehouse;
  if (itemName === "filestorage") return DESTINATION_RESOURCE_DEFAULTS.fileStorage;
  if (itemName === "externalsystem") return DESTINATION_RESOURCE_DEFAULTS.externalSystem;
  if (itemName === "schemaapi") return DESTINATION_RESOURCE_DEFAULTS.schemaAPI;
  return getDestinationResourceDefaults(productResource);
};

export const isRecordDeleted = (item) => getBoolean(item, "IsDeleted", "isDeleted");

export const isRecordActive = (item) => {
  const activeValue = getValue(item, "IsActive", "isActive");
  if (activeValue === undefined || activeValue === null || activeValue === "") return true;
  return getBoolean(item, "IsActive", "isActive");
};

export const toDestinationSetupDefaults = (defaults = {}) => ({
  ...defaults,
  DestinationItemName: defaults.itemName,
  DestinationRelationship: "SchemaDestination",
  DeploymentCollectionField: defaults.deploymentCollection
});

export const withDeploymentConnectionCheckId = (deployment) => {
  const deploymentId = getDeploymentId(deployment);
  return deployment
    ? {
      ...deployment,
      UniqueId: getString(deployment, "UniqueId", "uniqueId") || deploymentId,
      DeploymentId: getString(deployment, "DeploymentId", "deploymentId") || deploymentId
    }
    : deployment;
};
